import { Navigate, Route, Routes } from 'react-router';
import { Shell } from './components/Shell.js';
import { RequireAuth } from './components/RequireAuth.js';
import { RequirePermission } from './components/RequirePermission.js';
import { LoginPage } from './routes/LoginPage.js';
import { ForgotPasswordPage } from './routes/ForgotPasswordPage.js';
import { ResetPasswordPage } from './routes/ResetPasswordPage.js';
import { OverviewPage } from './routes/OverviewPage.js';
import { AnalyticsPage } from './routes/AnalyticsPage.js';
import { SecurityPage } from './routes/SecurityPage.js';
import { SettingsPage } from './routes/SettingsPage.js';
import { RolesPage } from './routes/RolesPage.js';
import { LeadsPage } from './routes/LeadsPage.js';
import { LeadDetailPage } from './routes/LeadDetailPage.js';
import { ClientsPage } from './routes/ClientsPage.js';
import { ClientDetailPage } from './routes/ClientDetailPage.js';
import { NewClientPage } from './routes/NewClientPage.js';
import { ProjectsPage } from './routes/ProjectsPage.js';
import { ProjectDetailPage } from './routes/ProjectDetailPage.js';
import { NewProjectPage } from './routes/NewProjectPage.js';
import { InvoicesPage } from './routes/InvoicesPage.js';
import { NewInvoicePage } from './routes/NewInvoicePage.js';
import { CarePlansPage } from './routes/CarePlansPage.js';
import { BlogPage } from './routes/BlogPage.js';
import { PostEditorPage } from './routes/PostEditorPage.js';
import { TestimonialsPage } from './routes/TestimonialsPage.js';
import { EditRequestsPage } from './routes/EditRequestsPage.js';
import { NotFoundPage } from './routes/NotFoundPage.js';
import { ForbiddenPage } from './routes/ForbiddenPage.js';

/* ============================================================================
   Routes.
   ----------------------------------------------------------------------------
   Every screen in the phase-8 plan exists as of 2026-09-12.

   Privacy requests will probably never be a screen, and that is a decision
   rather than an omission: the DPDP endpoints act immediately, so there is no
   request queued and nothing for a screen to work.
   ========================================================================== */

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      <Route
        element={
          <RequireAuth>
            <Shell />
          </RequireAuth>
        }
      >
        <Route path="/" element={<OverviewPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        {/* Open to any admin: the account half is theirs. The team and the
            business details are owner-only and the screen hides them, but the
            API is the gate — see settings.routes.ts. */}
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        {/* The permission matrix. Said as the permission rather than the role,
            like /invoices/new — though here they are the same thing: the API
            carries requireRole('owner') as well, because editing who may do
            what is not a grant anybody should be able to receive. */}
        <Route
          path="/settings/roles"
          element={
            <RequirePermission permission="settings:team">
              <RolesPage />
            </RequirePermission>
          }
        />
        <Route path="/leads" element={<LeadsPage />} />
        <Route path="/leads/:id" element={<LeadDetailPage />} />
        <Route path="/clients" element={<ClientsPage />} />
        {/* Before /clients/:id so "new" is never read as an id. */}
        <Route path="/clients/new" element={<NewClientPage />} />
        <Route path="/clients/:id" element={<ClientDetailPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        {/* Before /projects/:id, so "new" is never read as an id. */}
        <Route path="/projects/new" element={<NewProjectPage />} />
        <Route path="/projects/:id" element={<ProjectDetailPage />} />
        <Route path="/invoices" element={<InvoicesPage />} />
        {/* Raising an invoice is owner-only, and said as the permission
            rather than as the role: /403 beats a form the API will refuse
            once it has been filled in. */}
        <Route
          path="/invoices/new"
          element={
            <RequirePermission permission="invoices:create">
              <NewInvoicePage />
            </RequirePermission>
          }
        />
        <Route path="/care-plans" element={<CarePlansPage />} />
        <Route path="/blog" element={<BlogPage />} />
        {/* Before /blog/:id, so "new" is never read as an id. */}
        <Route path="/blog/new" element={<PostEditorPage />} />
        <Route path="/blog/:id" element={<PostEditorPage />} />
        <Route path="/edit-requests" element={<EditRequestsPage />} />
        <Route path="/testimonials" element={<TestimonialsPage />} />
      </Route>

      <Route path="/403" element={<ForbiddenPage />} />
      <Route path="/404" element={<NotFoundPage />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
}
